import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';
import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { Checkbox, makeStyles, Dropdown, Option } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SearchableMultiSelectorInput } from '@bingads-webui-campaign-react/searchable-multi-selector-input';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';

import { useAppStoreRepository } from './use-app-store-repository';
import { AppPanel } from './components/app-panel';
import { AppItem } from './components/app-item';
import { countries } from './constants/countries';

const useStyles = makeStyles({
  appStoreSelector: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 10,
    '& .fui-Combobox': {
      maxWidth: 'unset !important',
    },
  },
});

const UnvalidatedStoreAppSelector = ({
  platform,
  onChange,
  defaultSelected,
  placeholderText,
  readOnly,
  isCountryRefinementEnabled,
}) => {
  const i18n = useContext(I18nContext);

  const [selectedOption, setSelectedOption] = useState(defaultSelected ? [defaultSelected] : []);
  const [advancedSearchMode, setAdvancedSearchMode] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState([]);

  const { searchApp, searchAppManual } = useAppStoreRepository({
    platform,
  });

  const classes = useStyles();

  const placeholder = useMemo(
    () => {
      if (placeholderText) {
        return i18n.getString(placeholderText);
      }
      return advancedSearchMode ? i18n.getString(_TL_('Search for an app using app ID or app URL')) : i18n.getString(_TL_('Search for an app using name or app ID'));
    }
    , [advancedSearchMode, i18n, placeholderText]
  );

  const onInputChange = useCallback((searchValue) => {
    setSelectedOption(searchValue);
  }, []);

  const customRender = useCallback(option => (
    <AppItem
      appTitle={option.AppTitle}
      appLogoUrl={option.IconUrl}
      appCountryCode={option.CountryCode}
    />
  ), []);

  const onCountrySelect = useCallback((data) => {
    setSelectedCountry(data.selectedOptions);
  }, []);

  const manualSearch = useCallback((searchQuery) => {
    const countryCode = selectedCountry && selectedCountry[0];
    return searchAppManual(searchQuery, countryCode);
  }, [selectedCountry, searchAppManual]);

  const onRemoveApp = useCallback(() => {
    setSelectedOption([]);
    setSelectedCountry([]);
  }, []);

  useEffect(() => {
    onChange(selectedOption[0]);
  }, [onChange, selectedOption]);

  useEffect(() => {
    setSelectedOption(defaultSelected ? [defaultSelected] : []);
  }, [defaultSelected]);

  return (
    <div className={classNames('app-store-selection', classes.appStoreSelector)}>
      {
        selectedOption.length > 0 ? (
          <AppPanel
            selectedApp={selectedOption[0]}
            onRemoveApp={onRemoveApp}
            readOnly={readOnly}
          />
        ) : (
          <div className={classes.appStoreContainer}>
            <SearchableMultiSelectorInput
              className="app-store-selector__input"
              fetchFn={advancedSearchMode ? manualSearch : searchApp}
              value={selectedOption}
              onChange={onInputChange}
              placeholderText={placeholder}
              multiSelect={false}
              disableTags
              onRenderOption={customRender}
            />
            {isCountryRefinementEnabled && (
              <Checkbox
                label={
                  <span>
                    <em>{i18n.getString(_TL_('Note: '))}</em>
                    {i18n.getString(_TL_('If you’re unable to find your app, refine your search by selecting a country/region where the app is available.'))}
                  </span>
                }
                labelPosition="after"
                value={advancedSearchMode}
                onChange={(e, data) => setAdvancedSearchMode(data.checked)}
              />
            )}
            {
              advancedSearchMode && (
                <Dropdown
                  placeholder={i18n.getString(_TL_('Select a country'))}
                  onOptionSelect={(e, data) => onCountrySelect(data)}
                  selectedOptions={selectedCountry}
                >
                  {
                    _.map(_.sortBy(countries, 'name'), country => (
                      <Option key={country.code} value={country.code}>
                        {i18n.getString(country.name)}
                      </Option>
                    ))
                  }
                </Dropdown>
              )
            }
          </div>

        )
      }
    </div>
  );
};

UnvalidatedStoreAppSelector.propTypes = {
  platform: PropTypes.string.isRequired,
  defaultSelected: PropTypes.shape({}),
  onChange: PropTypes.func,
  placeholderText: PropTypes.string,
  readOnly: PropTypes.bool,
  isCountryRefinementEnabled: PropTypes.bool.isRequired,
};

UnvalidatedStoreAppSelector.defaultProps = {
  defaultSelected: null,
  onChange: () => {},
  placeholderText: null,
  readOnly: false,
};

const StoreAppSelectorInternal = ({
  updateValidation,
  isMounted,
  validationProps,
  isCountryRefinementEnabled,
  ...rest
}) => {
  const {
    data,
  } = validationProps;

  const onChange = useCallback((value) => {
    if (!_.isNull(value) && !_.isUndefined(value) && data && value !== data.AppSetting) {
      updateValidation([], { AppSetting: value });
    }
  }, [data, updateValidation]);

  useEffect(() => {
    if (isMounted && data && !data.AppSetting) {
      updateValidation([_TL_('Please select an app.')], null, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, updateValidation]);

  return (
    <UnvalidatedStoreAppSelector
      {...rest}
      defaultSelected={data && data.AppSetting}
      onChange={onChange}
      isCountryRefinementEnabled={isCountryRefinementEnabled}
    />
  );
};

StoreAppSelectorInternal.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      AppSetting: PropTypes.shape({}),
    }),
  }).isRequired,
  isCountryRefinementEnabled: PropTypes.bool,
};

StoreAppSelectorInternal.defaultProps = {
  isCountryRefinementEnabled: true,
};

export const StoreAppSelector = withError(withCustomValidation(StoreAppSelectorInternal));

